import { Box, Grid, Slide, Stack, Typography } from "@mui/material";
import {
  useAddSofrRateMutation,
  useGetSofrRatesQuery,
} from "api/api-operations";
import Layout from "components/Common/Layout";
import SkeletonLoad from "components/Common/SkeletonLoad";
import SOFRTable from "components/HealthCheck/SOFRTable";
import useGetCMASofrRates from "hooks/useGetCMASofrRates";
import useIsMobile from "hooks/useIsMobile";
import moment from "moment";
import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useAppSelector } from "redux/hooks";

interface SOFRRate {
  price: string;
  term: string;
  timestamp: string;
}

interface Rates {
  sofrRatesFixing: SOFRRate[];
}

interface ResultsStrip {
  date: string;
  rates: Rates;
  average30day: number;
  average90day: number;
  average180day: number;
  index: number;
  overnight: string;
}

const SOFRPage = () => {
  const isMobile = useIsMobile();
  const USER_PERMISSIONS = useAppSelector(
    (state) => state.appState.user_permission
  );
  const hasCalledAPI = useRef(false);
  const IS_MONDAY = moment().isoWeekday() === 1;
  const SOFR_DATE_LIMIT = moment()
    .subtract(IS_MONDAY ? 3 : 1, "days")
    .format("YYYY-MM-DD");
  const SOFR_RANGE_FROM = moment()
    .subtract(10, "days")
    .startOf("day")
    .format("YYYY-MM-DD");

  const {
    data: sofrRates,
    isLoading,
    refetch: refetchSofr,
  } = useGetSofrRatesQuery({
    from: SOFR_RANGE_FROM,
    to: SOFR_DATE_LIMIT,
  });
  const IS_SOFR_UPDATED = moment(sofrRates?.[0]?.date).isSame(
    SOFR_DATE_LIMIT,
    "day"
  );
  const [updateSofr, { isLoading: isUpdating }] = useAddSofrRateMutation();

  const { data: cmaSOFR, isFetching, fetchSofrRates } = useGetCMASofrRates();

  // Automatically update SOFR rates if they are not updated
  useEffect(() => {
    if (!isLoading && sofrRates && !IS_SOFR_UPDATED && !hasCalledAPI.current) {
      hasCalledAPI.current = true;
      fetchSofrRates()
        .then((fetchedData) => {
          // Use the returned data directly instead of cmaSOFR state
          const sofrRate = fetchedData?.find((sofr) => {
            return moment(sofr.date).format("YYYY-MM-DD") === SOFR_DATE_LIMIT;
          });
          if (sofrRate) {
            updateSofr({
              date: moment(sofrRate.date).format("YYYY-MM-DD"),
              rate:
                parseFloat(
                  sofrRate.rates.sofrRatesFixing.find(
                    (rate) => rate.term === "3M"
                  )?.price ?? "0.00"
                ) ?? 0,
            })
              .unwrap()
              .then(() => {
                toast.success("SOFR rates updated successfully");
                refetchSofr();
              })
              .catch((e) => {
                toast.error(`Failed to save SOFR rates: ${e?.message}`);
                hasCalledAPI.current = false; // Reset on error to allow retry
              });
          } else {
            toast.error("SOFR rates unavailable for today");
            hasCalledAPI.current = false; // Reset on error to allow retry
          }
        })
        .catch((e) => {
          toast.error(`Failed to fetch SOFR rates: ${e?.message}`);
          hasCalledAPI.current = false; // Reset on error to allow retry
        });
    }
  }, [isLoading, sofrRates, IS_SOFR_UPDATED]);

  return (
    <Slide in direction="left">
      <Box flexGrow={1}>
        <Layout
          title="SOFR Rates"
          textHeadingOptions={{
            fontWeight: "bolder",
            level: 2,
            fontSize: isMobile ? "1.2em" : "1.5em",
          }}
          headerConfig={{
            left: 12,
            mid: 0,
            right: 0,
            syncAction: refetchSofr,
          }}
          mode="default"
          alignItems="center"
        >
          {/**transaction list */}
          <Grid item xs={12}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={4}
            >
              <Grid item xs={12}>
                <Grid
                  container
                  spacing={1}
                  justifyContent="start"
                  alignItems="end"
                >
                  <Grid item xs={12} lg={12}>
                    {(isFetching || isUpdating) && !IS_SOFR_UPDATED && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography color="primary">
                          Updating SOFR rates for{" "}
                          {moment(SOFR_DATE_LIMIT).format("Do MMM 'YY")}...
                        </Typography>
                      </Stack>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    {isLoading && <SkeletonLoad bars={10} />}
                    {!isLoading && sofrRates && (
                      <SOFRTable data={sofrRates} showAll />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Layout>
      </Box>
    </Slide>
  );
};
export default SOFRPage;
